import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Euro,
  Calendar,
  PieChart,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Wallet,
  Percent,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MOIS_LABELS = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Avr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Aou',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec'
};

const MOIS_LONGS = {
  '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
  '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
  '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
};

// Palette dataviz : brut = teal marque, cotisations patronales = teal clair (même famille),
// cumul = ambre (série d'une autre nature), théorique = même couleur atténuée.
const CHART_COLORS = {
  brut: '#0F5156',
  patronales: '#7FB3B6',
  cumul: '#B45309',
  reel: '#0F5156',
  theorique: '#94A3B8',
  grid: '#e2e8f0',
  axis: '#64748b'
};

const TOOLTIP_STYLE = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
  fontSize: '12px'
};

const fmtEur = (value, digits = 0) => (Number(value) || 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits
});
const fmtPct = (value) => (value === null || value === undefined || Number.isNaN(Number(value)))
  ? '—'
  : `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
const fmtAxisEur = (value) => Math.abs(value) >= 1000 ? `${Math.round(value / 1000)} k€` : `${value} €`;
const moisCourt = (periode) => MOIS_LABELS[String(periode).slice(5, 7)] || periode;
const moisLong = (periode) => MOIS_LONGS[String(periode).slice(5, 7)] || periode;

const SOURCE_META = {
  reel: { label: 'Réel', className: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200' },
  theorique: { label: 'Théorique', className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300' },
  mixte: { label: 'Mixte', className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200' },
  estime: { label: 'Estimé', className: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200' },
  reel_partiel: { label: 'Réel partiel', className: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200' },
  aucune: { label: 'Aucune donnée', className: 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400' }
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const Reporting = () => {
  const [activeTab, setActiveTab] = useState('masse-salariale');
  const [loading, setLoading] = useState(true);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [mode, setMode] = useState('mixte');
  const [masseSalariale, setMasseSalariale] = useState(null);
  const [turnover, setTurnover] = useState(null);
  const [absenteisme, setAbsenteisme] = useState(null);
  const [pyramide, setPyramide] = useState(null);
  const [effectifs, setEffectifs] = useState(null);
  const [coutReel, setCoutReel] = useState(null);
  const [pluriannuel, setPluriannuel] = useState(null);
  const [masseTheorique, setMasseTheorique] = useState(null);
  const [salaries, setSalaries] = useState(null);
  const [salariesOpen, setSalariesOpen] = useState(false);
  const [salariesLoading, setSalariesLoading] = useState(false);
  const [salariesForbidden, setSalariesForbidden] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [recomputeJob, setRecomputeJob] = useState(null);
  const recomputeTimer = useRef(null);
  const tabInitialized = useRef(false);

  useEffect(() => () => { if (recomputeTimer.current) clearInterval(recomputeTimer.current); }, []);

  const handleRecompute = async () => {
    try {
      const res = await axios.post(`${API_URL}/payroll/recompute-amounts?annee=${annee}`, null, { headers: authHeaders() });
      setRecomputeJob({ id: res.data.job_id, statut: 'en_cours', total: 0, traites: 0 });
      if (recomputeTimer.current) clearInterval(recomputeTimer.current);
      recomputeTimer.current = setInterval(async () => {
        try {
          const job = await axios.get(`${API_URL}/payroll/import-jobs/${res.data.job_id}`, { headers: authHeaders() });
          setRecomputeJob(job.data);
          if (job.data.statut !== 'en_cours') {
            clearInterval(recomputeTimer.current);
            recomputeTimer.current = null;
            if (job.data.statut === 'erreur') toast.error(job.data.erreur || 'Recalcul en erreur');
            else toast.success(`Recalcul terminé : ${job.data.resultat?.updated_count || 0} bulletin(s) mis à jour (${job.data.resultat?.complets || 0} complets)`);
            setRecomputeJob(null);
            fetchAllData();
          }
        } catch (e) {
          clearInterval(recomputeTimer.current);
          recomputeTimer.current = null;
          setRecomputeJob(null);
        }
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de lancer le recalcul');
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annee, mode]);

  const fetchAllData = async () => {
    setLoading(true);
    setSalaries(null);
    setSalariesOpen(false);
    const headers = authHeaders();
    try {
      const [masseRes, turnoverRes, absRes, pyrRes, effRes, coutRes, pluriRes, theoRes] = await Promise.all([
        axios.get(`${API_URL}/reporting/masse-salariale?annee=${annee}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/reporting/turnover?annee=${annee}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/reporting/absenteisme?annee=${annee}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/reporting/pyramide-ages?annee=${annee}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/reporting/effectifs?annee=${annee}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/reporting/cout-reel?annee=${annee}&mode=${mode}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/reporting/cout-reel/pluriannuel?debut=${annee - 2}&fin=${annee}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/reporting/masse-theorique?annee=${annee}`, { headers }).catch(() => ({ data: null }))
      ]);
      setMasseSalariale(masseRes.data);
      setTurnover(turnoverRes.data);
      setAbsenteisme(absRes.data);
      setPyramide(pyrRes.data);
      setEffectifs(effRes.data);
      setCoutReel(coutRes.data);
      setPluriannuel(pluriRes.data);
      setMasseTheorique(theoRes.data);
      // Onglet par défaut : « Coût réel » dès qu'un logiciel de paie est connecté
      if (!tabInitialized.current) {
        tabInitialized.current = true;
        if (coutRes.data?.payroll_connected) setActiveTab('cout-reel');
      }
    } catch (error) {
      console.error('Error fetching reporting data:', error);
      toast.error('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaries = async () => {
    setSalariesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/reporting/cout-reel/salaries?annee=${annee}`, { headers: authHeaders() });
      setSalaries(res.data?.salaries || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setSalariesForbidden(true);
        setSalariesOpen(false);
      } else {
        toast.error('Erreur lors du chargement du détail par salarié');
      }
    } finally {
      setSalariesLoading(false);
    }
  };

  const toggleSalaries = () => {
    const next = !salariesOpen;
    setSalariesOpen(next);
    if (next && salaries === null) fetchSalaries();
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/reporting/cout-reel/export?annee=${annee}&mode=${mode}&format=csv`, {
        headers: authHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cout_reel_${annee}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error("Erreur lors de l'export CSV");
    } finally {
      setExporting(false);
    }
  };

  // Données des graphiques « Coût réel »
  const coutMois = coutReel?.mois || [];
  const chartMensuel = coutMois.map((m) => ({
    name: moisCourt(m.periode), periode: m.periode, source: m.source,
    brut: Math.round(m.brut || 0), patronales: Math.round(m.cotisations_patronales || 0)
  }));
  let cumulCourant = 0;
  const chartCumul = coutMois.map((m) => {
    cumulCourant += m.cout_employeur || 0;
    return { name: moisCourt(m.periode), cumul: Math.round(cumulCourant), source: m.source };
  });
  const chartAnnees = (pluriannuel?.annees || []).map((a) => ({
    name: String(a.annee), reel: Math.round(a.cout_employeur || 0), theorique: Math.round(a.cout_employeur_theorique || 0),
    nb_bulletins: a.nb_bulletins
  }));
  const couvertureParPeriode = Object.fromEntries((coutReel?.couverture?.mois || []).map((c) => [c.periode, c]));
  const variationN1 = coutReel?.comparaison_n1?.variation_pct;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Reporting RH</h1>
          <p className="text-slate-600 dark:text-slate-400">Statistiques et indicateurs cles des ressources humaines</p>
        </div>
        <Select value={String(annee)} onValueChange={(v) => setAnnee(parseInt(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Global KPI */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${effectifs?.sorties_annee !== undefined ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Effectif actuel</p>
                <p className="text-xl font-bold">{effectifs?.actifs || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {effectifs?.sorties_annee !== undefined && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Sorties sur l'année</p>
                  <p className="text-xl font-bold">{effectifs.sorties_annee}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Euro className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Masse salariale/mois</p>
                <p className="text-xl font-bold">{masseSalariale?.masse_brute_mensuelle?.toLocaleString('fr-FR') || 0} &euro;</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Euro className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Salaire moyen</p>
                <p className="text-xl font-bold">{masseSalariale?.salaire_moyen?.toLocaleString('fr-FR') || 0} &euro;</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Turnover</p>
                <p className="text-xl font-bold">{turnover?.taux_turnover || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Absenteisme</p>
                <p className="text-xl font-bold">{absenteisme?.taux_absenteisme || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="cout-reel">Coût réel (paie)</TabsTrigger>
          <TabsTrigger value="masse-salariale">Masse salariale</TabsTrigger>
          <TabsTrigger value="effectifs">Effectifs</TabsTrigger>
          <TabsTrigger value="turnover">Turnover</TabsTrigger>
          <TabsTrigger value="absenteisme">Absenteisme</TabsTrigger>
          <TabsTrigger value="pyramide">Pyramide des ages</TabsTrigger>
        </TabsList>

        {/* Coût réel de la masse salariale (bulletins de paie) */}
        <TabsContent value="cout-reel" className="space-y-4">
          {coutReel ? (
            <>
              <SourceBanner data={coutReel} />

              {/* Barre d'actions */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <span>Mode :</span>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger className="w-[190px] h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mixte">Mixte (réel puis théorique)</SelectItem>
                      <SelectItem value="reel">Réel uniquement</SelectItem>
                      <SelectItem value="theorique">Théorique uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={exporting}>
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Exporter CSV
                </Button>
              </div>

              {/* KPI coût réel */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <KpiCard icon={Wallet} tone="teal" label={`Coût employeur ${annee}`} value={fmtEur(coutReel.totaux?.cout_employeur)} />
                <KpiCard icon={Euro} tone="green" label="Brut annuel" value={fmtEur(coutReel.totaux?.brut)} />
                <KpiCard icon={BarChart3} tone="blue" label="Cotisations patronales" value={fmtEur(coutReel.totaux?.cotisations_patronales)} />
                <KpiCard icon={Percent} tone="amber" label="Taux de charges constaté" value={fmtPct(coutReel.totaux?.taux_charges_patronales_constate)} />
                <KpiCard
                  icon={variationN1 === null || variationN1 === undefined ? Minus : (variationN1 >= 0 ? ArrowUpRight : ArrowDownRight)}
                  tone={variationN1 === null || variationN1 === undefined ? 'slate' : (variationN1 >= 0 ? 'red' : 'green')}
                  label={`Variation vs ${annee - 1}`}
                  value={variationN1 === null || variationN1 === undefined ? '—' : `${variationN1 > 0 ? '+' : ''}${fmtPct(variationN1)}`}
                  hint={coutReel.comparaison_n1?.cout_employeur ? `${fmtEur(coutReel.comparaison_n1.cout_employeur)} en ${annee - 1}` : `Pas de données ${annee - 1}`}
                />
              </div>

              {coutReel.couverture?.alerte && (
                <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-sm">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">Données incomplètes</p>
                    <p className="text-amber-700 dark:text-amber-300">
                      Certains mois comptent moins de bulletins que l'effectif attendu (voir la colonne Couverture).
                      {' '}<Link to="/payroll" className="underline font-medium">Importer les bulletins manquants</Link>
                    </p>
                  </div>
                </div>
              )}

              {(coutReel.completude?.nb_reel_partiel > 0) && (
                <div className="flex items-start gap-3 p-4 rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800 text-sm">
                  <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-orange-800 dark:text-orange-200">
                      {coutReel.completude.nb_reel_partiel} bulletin(s) sans cotisations patronales
                    </p>
                    <p className="text-orange-700 dark:text-orange-300">
                      Le brut est réel mais {coutReel.provider_name || 'le logiciel de paie'} n'a pas fourni les cotisations : elles sont estimées
                      ({coutReel.estimation?.methode_label?.toLowerCase()}). Relancez la lecture des montants depuis {coutReel.provider_name || 'le logiciel de paie'}.
                    </p>
                  </div>
                  {coutReel.payroll_connected && (
                    <Button variant="outline" size="sm" onClick={handleRecompute} disabled={!!recomputeJob}>
                      {recomputeJob ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                      {recomputeJob ? `Recalcul... ${recomputeJob.traites || 0}/${recomputeJob.total || '?'}` : 'Recalculer les montants'}
                    </Button>
                  )}
                </div>
              )}

              {/* Graphiques */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Brut et cotisations patronales par mois</CardTitle>
                    <p className="text-xs text-slate-500">Mois réels en couleur pleine, mois mixtes atténués, mois estimés très atténués</p>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartMensuel} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                          <XAxis dataKey="name" stroke={CHART_COLORS.axis} fontSize={12} tickLine={false} />
                          <YAxis stroke={CHART_COLORS.axis} fontSize={12} tickFormatter={fmtAxisEur} tickLine={false} axisLine={false} width={64} />
                          <Tooltip
                            contentStyle={TOOLTIP_STYLE}
                            formatter={(value, name) => [fmtEur(value), name]}
                            labelFormatter={(label, payload) => {
                              const p = payload?.[0]?.payload;
                              return p ? `${moisLong(p.periode)} ${annee} · ${SOURCE_META[p.source]?.label || p.source}` : label;
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                          <Bar dataKey="brut" stackId="cout" name="Brut" fill={CHART_COLORS.brut}>
                            {chartMensuel.map((d) => <Cell key={d.periode} fillOpacity={d.source === 'reel' ? 1 : (d.source === 'mixte' ? 0.7 : 0.4)} />)}
                          </Bar>
                          <Bar dataKey="patronales" stackId="cout" name="Cotisations patronales" fill={CHART_COLORS.patronales} radius={[3, 3, 0, 0]}>
                            {chartMensuel.map((d) => <Cell key={d.periode} fillOpacity={d.source === 'reel' ? 1 : (d.source === 'mixte' ? 0.7 : 0.4)} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Coût employeur cumulé</CardTitle>
                    <p className="text-xs text-slate-500">Cumul depuis janvier {annee}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartCumul} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                          <XAxis dataKey="name" stroke={CHART_COLORS.axis} fontSize={12} tickLine={false} />
                          <YAxis stroke={CHART_COLORS.axis} fontSize={12} tickFormatter={fmtAxisEur} tickLine={false} axisLine={false} width={64} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => [fmtEur(value), 'Cumul']} />
                          <Line type="monotone" dataKey="cumul" name="Coût employeur cumulé" stroke={CHART_COLORS.cumul} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Comparaison pluriannuelle</CardTitle>
                    <p className="text-xs text-slate-500">Coût employeur annuel réel (bulletins) face à l'estimation théorique</p>
                  </CardHeader>
                  <CardContent>
                    {chartAnnees.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartAnnees} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                            <XAxis dataKey="name" stroke={CHART_COLORS.axis} fontSize={12} tickLine={false} />
                            <YAxis stroke={CHART_COLORS.axis} fontSize={12} tickFormatter={fmtAxisEur} tickLine={false} axisLine={false} width={64} />
                            <Tooltip
                              contentStyle={TOOLTIP_STYLE}
                              formatter={(value, name, item) => [
                                name === 'Réel (bulletins)' ? `${fmtEur(value)} · ${item?.payload?.nb_bulletins || 0} bulletin(s)` : fmtEur(value), name
                              ]}
                            />
                            <Legend wrapperStyle={{ fontSize: '12px' }} />
                            <Bar dataKey="reel" name="Réel (bulletins)" fill={CHART_COLORS.reel} radius={[3, 3, 0, 0]} />
                            <Bar dataKey="theorique" name="Théorique estimé" fill={CHART_COLORS.theorique} radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <p className="text-sm text-slate-500">Aucune donnée pluriannuelle</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Tableau mensuel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Détail mois par mois</CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Mois</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Effectif payé</TableHead>
                        <TableHead className="text-right">Brut</TableHead>
                        <TableHead className="text-right">Cot. salariales</TableHead>
                        <TableHead className="text-right">Cot. patronales</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-right">Coût employeur</TableHead>
                        <TableHead className="text-right">Taux charges</TableHead>
                        <TableHead className="text-right">Couverture</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coutMois.map((m) => {
                        const c = couvertureParPeriode[m.periode];
                        return (
                          <TableRow key={m.periode} className={m.futur ? 'text-slate-400' : ''}>
                            <TableCell className="font-medium">{moisLong(m.periode)}</TableCell>
                            <TableCell>
                              <SourceBadge source={m.source} />
                              {m.futur && m.source !== 'reel' && <span className="ml-1 text-xs text-slate-400">prévisionnel</span>}
                              {(m.nb_reel || m.nb_reel_partiel || m.nb_estime) ? (
                                <span className="block text-[11px] text-slate-500 mt-0.5">
                                  {m.nb_reel || 0} réel{(m.nb_reel || 0) > 1 ? 's' : ''}
                                  {m.nb_reel_partiel > 0 && ` · ${m.nb_reel_partiel} partiel${m.nb_reel_partiel > 1 ? 's' : ''}`}
                                  {m.nb_estime > 0 && ` · ${m.nb_estime} estimé${m.nb_estime > 1 ? 's' : ''}`}
                                </span>
                              ) : null}
                              {m.bulletins_sans_montants > 0 && (
                                <span className="ml-1 text-xs text-amber-600" title="Bulletins importés sans montants (brut absent)">
                                  {m.bulletins_sans_montants} sans montant
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{m.effectif_paye}</TableCell>
                            <TableCell className="text-right">{fmtEur(m.brut, 2)}</TableCell>
                            <TableCell className="text-right">{fmtEur(m.cotisations_salariales, 2)}</TableCell>
                            <TableCell className="text-right">{fmtEur(m.cotisations_patronales, 2)}</TableCell>
                            <TableCell className="text-right">{fmtEur(m.net, 2)}</TableCell>
                            <TableCell className="text-right font-semibold">{fmtEur(m.cout_employeur, 2)}</TableCell>
                            <TableCell className="text-right">{fmtPct(m.taux_charges_patronales_constate)}</TableCell>
                            <TableCell className="text-right">
                              {c && c.effectif_attendu > 0 ? (
                                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${c.incomplet ? 'bg-amber-100 text-amber-800 border-amber-200' : (c.bulletins > 0 ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200')}`}>
                                  {c.incomplet && <AlertTriangle className="w-3 h-3" />}
                                  {c.bulletins}/{c.effectif_attendu}
                                </span>
                              ) : <span className="text-xs text-slate-400">—</span>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-slate-50 dark:bg-slate-800 font-semibold">
                        <TableCell>Total {annee}</TableCell>
                        <TableCell className="text-xs font-normal text-slate-500">
                          {coutReel.totaux?.mois_reels || 0} mois réels · {coutReel.totaux?.mois_mixtes || 0} mixtes · {coutReel.totaux?.mois_theoriques || 0} estimés
                          {coutReel.totaux?.part_reel_pct !== null && coutReel.totaux?.part_reel_pct !== undefined && (
                            <span className="block">{fmtPct(coutReel.totaux.part_reel_pct)} des lignes salarié × mois en réel</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{coutReel.totaux?.effectif_moyen_paye} <span className="text-xs font-normal text-slate-500">moy.</span></TableCell>
                        <TableCell className="text-right">{fmtEur(coutReel.totaux?.brut, 2)}</TableCell>
                        <TableCell className="text-right">{fmtEur(coutReel.totaux?.cotisations_salariales, 2)}</TableCell>
                        <TableCell className="text-right">{fmtEur(coutReel.totaux?.cotisations_patronales, 2)}</TableCell>
                        <TableCell className="text-right">{fmtEur(coutReel.totaux?.net, 2)}</TableCell>
                        <TableCell className="text-right">{fmtEur(coutReel.totaux?.cout_employeur, 2)}</TableCell>
                        <TableCell className="text-right">{fmtPct(coutReel.totaux?.taux_charges_patronales_constate)}</TableCell>
                        <TableCell className="text-right text-xs font-normal text-slate-500">{coutReel.totaux?.nb_bulletins || 0} bulletins</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Ventilations */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <BreakdownCard title="Par département" data={coutReel.par_departement} total={coutReel.totaux?.cout_employeur} />
                <BreakdownCard title="Par type de contrat" data={coutReel.par_type_contrat} total={coutReel.totaux?.cout_employeur} capitalize />
                <BreakdownCard title="Par statut professionnel" data={coutReel.par_statut_professionnel} total={coutReel.totaux?.cout_employeur} capitalize />
              </div>

              {/* Familles de cotisations et écart réel / estimé */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FamillesCard familles={coutReel.familles} brut={coutReel.totaux?.brut} />
                <EcartCard ecart={coutReel.ecart_reel_estime} estimation={coutReel.estimation} />
              </div>

              {/* Détail par salarié (selon permission salaire) */}
              {!salariesForbidden && (
                <Card>
                  <CardHeader className="cursor-pointer" onClick={toggleSalaries}>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Détail par salarié</CardTitle>
                        <p className="text-xs text-slate-500">Consolidé {annee} : bulletins (réel), bulletins sans cotisations (réel partiel) et estimation</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleSalaries(); }}>
                        {salariesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </CardHeader>
                  {salariesOpen && (
                    <CardContent className="overflow-x-auto">
                      {salariesLoading ? (
                        <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Chargement...</div>
                      ) : (salaries && salaries.length > 0) ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Salarié</TableHead>
                              <TableHead>Département</TableHead>
                              <TableHead>Contrat</TableHead>
                              <TableHead>Source</TableHead>
                              <TableHead className="text-right">Bulletins</TableHead>
                              <TableHead className="text-right">Brut</TableHead>
                              <TableHead className="text-right">Cot. salariales</TableHead>
                              <TableHead className="text-right">Cot. patronales</TableHead>
                              <TableHead className="text-right">Net</TableHead>
                              <TableHead className="text-right">Coût employeur</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {salaries.map((s) => (
                              <TableRow key={s.employee_id}>
                                <TableCell className="font-medium">{s.nom} {s.prenom}{s.matricule ? <span className="ml-1 text-xs text-slate-400">({s.matricule})</span> : null}</TableCell>
                                <TableCell>{s.departement}</TableCell>
                                <TableCell className="capitalize">{s.type_contrat}</TableCell>
                                <TableCell>
                                  <SourceBadge source={s.source} />
                                  <span className="block text-[11px] text-slate-500">{s.nb_reel || 0} réel · {s.nb_reel_partiel || 0} partiel · {s.nb_estime || 0} estimé</span>
                                </TableCell>
                                <TableCell className="text-right">{s.nb_bulletins}</TableCell>
                                <TableCell className="text-right">{fmtEur(s.brut, 2)}</TableCell>
                                <TableCell className="text-right">{fmtEur(s.cotisations_salariales, 2)}</TableCell>
                                <TableCell className="text-right">{fmtEur(s.cotisations_patronales, 2)}</TableCell>
                                <TableCell className="text-right">{fmtEur(s.net, 2)}</TableCell>
                                <TableCell className="text-right font-semibold">{fmtEur(s.cout_employeur, 2)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-sm text-slate-500 py-2">Aucune donnée de paie pour {annee}.</p>
                      )}
                    </CardContent>
                  )}
                </Card>
              )}
            </>
          ) : <EmptyState />}
        </TabsContent>

        {/* Masse Salariale */}
        <TabsContent value="masse-salariale" className="space-y-4">
          {masseSalariale ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Par departement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(masseSalariale.par_departement || {}).sort((a, b) => b[1].masse_mensuelle - a[1].masse_mensuelle).map(([dept, data]) => (
                      <div key={dept} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{dept}</span>
                          <span>{data.masse_mensuelle?.toLocaleString('fr-FR')} &euro;/mois ({data.effectif} emp.)</span>
                        </div>
                        <Progress value={masseSalariale.masse_brute_mensuelle > 0 ? (data.masse_mensuelle / masseSalariale.masse_brute_mensuelle) * 100 : 0} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Par type de contrat</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(masseSalariale.par_type_contrat || {}).map(([type, data]) => (
                      <div key={type} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div>
                          <p className="font-medium capitalize">{type}</p>
                          <p className="text-sm text-slate-500">{data.effectif} employe(s)</p>
                        </div>
                        <span className="font-semibold">{data.masse_mensuelle?.toLocaleString('fr-FR')} &euro;/mois</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <EstimationChargesCard data={masseTheorique} onVoirCoutReel={() => setActiveTab('cout-reel')} />
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Synthese</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-[#0F5156]">{masseSalariale.masse_brute_annuelle?.toLocaleString('fr-FR')} &euro;</p>
                      <p className="text-sm text-slate-500">Masse annuelle brute</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold">{masseSalariale.salaire_moyen?.toLocaleString('fr-FR')} &euro;</p>
                      <p className="text-sm text-slate-500">Salaire moyen</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold">{masseSalariale.salaire_median?.toLocaleString('fr-FR')} &euro;</p>
                      <p className="text-sm text-slate-500">Salaire median</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold">{masseSalariale.effectif_total}</p>
                      <p className="text-sm text-slate-500">Effectif total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : <EmptyState />}
        </TabsContent>

        {/* Effectifs */}
        <TabsContent value="effectifs" className="space-y-4">
          {effectifs ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Repartition par departement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(effectifs.par_departement || {}).sort((a, b) => b[1] - a[1]).map(([dept, count]) => (
                      <div key={dept} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{dept}</span>
                          <span>{count} employe(s)</span>
                        </div>
                        <Progress value={effectifs.actifs > 0 ? (count / effectifs.actifs) * 100 : 0} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Par type de contrat</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(effectifs.par_type_contrat || {}).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <span className="font-medium capitalize">{type}</span>
                        <Badge>{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Temps de travail</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{effectifs.actifs}</p>
                      <p className="text-sm text-slate-500">Actifs</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{effectifs.par_temps_travail?.temps_plein || 0}</p>
                      <p className="text-sm text-slate-500">Temps plein</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{effectifs.par_temps_travail?.temps_partiel || 0}</p>
                      <p className="text-sm text-slate-500">Temps partiel</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : <EmptyState />}
        </TabsContent>

        {/* Turnover */}
        <TabsContent value="turnover" className="space-y-4">
          {turnover ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Entrees / Sorties par mois</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(turnover.par_mois || {}).map(([mois, data]) => {
                      const moisLabel = MOIS_LABELS[mois.split('-')[1]] || mois;
                      return (
                        <div key={mois} className="flex items-center gap-3 text-sm">
                          <span className="w-10 font-medium text-slate-500">{moisLabel}</span>
                          <div className="flex-1 flex items-center gap-2">
                            <div className="flex items-center gap-1 text-green-600">
                              <ArrowUpRight className="w-3 h-3" />
                              <span>{data.entrees}</span>
                            </div>
                            <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden flex">
                              {data.entrees > 0 && <div className="bg-green-500 h-full" style={{width: `${Math.min(data.entrees * 20, 50)}%`}}></div>}
                              {data.sorties > 0 && <div className="bg-red-500 h-full ml-auto" style={{width: `${Math.min(data.sorties * 20, 50)}%`}}></div>}
                            </div>
                            <div className="flex items-center gap-1 text-red-600">
                              <ArrowDownRight className="w-3 h-3" />
                              <span>{data.sorties}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Synthese {annee}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{turnover.entrees}</p>
                        <p className="text-sm text-slate-500">Entrees</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{turnover.sorties}</p>
                        <p className="text-sm text-slate-500">Sorties</p>
                      </div>
                    </div>
                    <div className="mt-4 text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-3xl font-bold text-[#0F5156]">{turnover.taux_turnover}%</p>
                      <p className="text-sm text-slate-500">Taux de turnover</p>
                    </div>
                  </CardContent>
                </Card>
                {Object.keys(turnover.motifs_sortie || {}).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Motifs de sortie</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(turnover.motifs_sortie).sort((a, b) => b[1] - a[1]).map(([motif, count]) => (
                          <div key={motif} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                            <span className="text-sm capitalize">{motif || 'Non renseigné'}</span>
                            <Badge variant="outline">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          ) : <EmptyState />}
        </TabsContent>

        {/* Absenteisme */}
        <TabsContent value="absenteisme" className="space-y-4">
          {absenteisme ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Par type d'absence</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(absenteisme.par_type || {}).sort((a, b) => b[1] - a[1]).map(([type, jours]) => {
                      const labels = { 'cp': 'Conges payes', 'rtt': 'RTT', 'maladie': 'Maladie', 'sans_solde': 'Sans solde', 'formation': 'Formation', 'evenement_familial': 'Evenement familial', 'autre': 'Autre' };
                      return (
                        <div key={type} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{labels[type] || type}</span>
                            <span>{jours} jour(s)</span>
                          </div>
                          <Progress value={absenteisme.total_jours_absence > 0 ? (jours / absenteisme.total_jours_absence) * 100 : 0} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Par mois</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(absenteisme.par_mois || {}).sort().map(([mois, jours]) => {
                      const moisLabel = MOIS_LABELS[mois.split('-')[1]] || mois;
                      const maxJours = Math.max(...Object.values(absenteisme.par_mois || {}), 1);
                      return (
                        <div key={mois} className="flex items-center gap-3 text-sm">
                          <span className="w-10 font-medium text-slate-500">{moisLabel}</span>
                          <div className="flex-1 h-4 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className="bg-purple-500 h-full rounded-full" style={{width: `${(jours / maxJours) * 100}%`}}></div>
                          </div>
                          <span className="w-12 text-right">{jours}j</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Indicateurs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-[#0F5156]">{absenteisme.taux_absenteisme}%</p>
                      <p className="text-sm text-slate-500">Taux d'absenteisme</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold">{absenteisme.total_jours_absence}</p>
                      <p className="text-sm text-slate-500">Jours d'absence</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold">{absenteisme.jours_moyen_par_employe}</p>
                      <p className="text-sm text-slate-500">Jours/employe</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold">{absenteisme.effectif}</p>
                      <p className="text-sm text-slate-500">Effectif</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : <EmptyState />}
        </TabsContent>

        {/* Pyramide des ages */}
        <TabsContent value="pyramide" className="space-y-4">
          {pyramide ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pyramide des ages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(pyramide.tranches || {}).map(([tranche, data]) => {
                      const maxCount = Math.max(...Object.values(pyramide.tranches).map(t => t.count), 1);
                      return (
                        <div key={tranche} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{tranche}</span>
                            <span>{data.count} employe(s)</span>
                          </div>
                          <div className="flex gap-1 h-4">
                            <div className="flex-1 flex justify-end">
                              <div className="bg-blue-500 rounded-l h-full" style={{width: `${maxCount > 0 ? (data.hommes / maxCount) * 100 : 0}%`}}></div>
                            </div>
                            <div className="flex-1">
                              <div className="bg-pink-500 rounded-r h-full" style={{width: `${maxCount > 0 ? (data.femmes / maxCount) * 100 : 0}%`}}></div>
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-slate-500">
                            <span>H: {data.hommes}</span>
                            <span>F: {data.femmes}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-6 mt-4 text-sm">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-500 rounded"></div>Hommes</div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-pink-500 rounded"></div>Femmes</div>
                  </div>
                </CardContent>
              </Card>
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Indicateurs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold text-[#0F5156]">{pyramide.age_moyen} ans</p>
                        <p className="text-sm text-slate-500">Age moyen</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold">{pyramide.anciennete_moyenne} ans</p>
                        <p className="text-sm text-slate-500">Anciennete moyenne</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Repartition H/F</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{pyramide.repartition_sexe?.hommes || 0}</p>
                        <p className="text-sm text-slate-500">Hommes</p>
                      </div>
                      <div className="text-center p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-pink-600">{pyramide.repartition_sexe?.femmes || 0}</p>
                        <p className="text-sm text-slate-500">Femmes</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold">{pyramide.repartition_sexe?.non_renseigne || 0}</p>
                        <p className="text-sm text-slate-500">Non renseigné</p>
                      </div>
                    </div>
                    {pyramide.effectif > 0 && (
                      <div className="mt-4">
                        <div className="flex h-4 rounded-full overflow-hidden">
                          <div className="bg-blue-500" style={{width: `${(pyramide.repartition_sexe?.hommes / pyramide.effectif) * 100}%`}}></div>
                          <div className="bg-pink-500" style={{width: `${(pyramide.repartition_sexe?.femmes / pyramide.effectif) * 100}%`}}></div>
                          <div className="bg-slate-300" style={{width: `${(pyramide.repartition_sexe?.non_renseigne / pyramide.effectif) * 100}%`}}></div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : <EmptyState />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const EmptyState = () => (
  <Card>
    <CardContent className="p-8 text-center text-slate-500">
      Aucune donnee disponible
    </CardContent>
  </Card>
);

const SourceBadge = ({ source }) => {
  const meta = SOURCE_META[source] || SOURCE_META.aucune;
  return <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${meta.className}`}>{meta.label}</span>;
};

const KPI_TONES = {
  teal: 'bg-[#0F5156]/10 text-[#0F5156]',
  green: 'bg-green-100 text-green-600',
  blue: 'bg-blue-100 text-blue-600',
  amber: 'bg-amber-100 text-amber-600',
  red: 'bg-red-100 text-red-600',
  slate: 'bg-slate-100 text-slate-600'
};

const KpiCard = ({ icon: Icon, tone = 'teal', label, value, hint }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${KPI_TONES[tone] || KPI_TONES.teal}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 truncate">{label}</p>
          <p className="text-xl font-bold truncate">{value}</p>
          {hint && <p className="text-xs text-slate-400 truncate">{hint}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const SourceBanner = ({ data }) => {
  const { source, payroll_connected: connected, provider_name: providerName, totaux, completude, estimation } = data;
  const tone = source === 'reel'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-100'
    : source === 'mixte'
      ? 'border-blue-200 bg-blue-50 text-blue-900 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-100'
      : 'border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100';
  const nbReel = completude?.nb_reel || 0;
  const nbPartiel = completude?.nb_reel_partiel || 0;
  const nbEstime = completude?.nb_estime || 0;
  let message;
  if (source === 'reel') {
    message = `Montants issus des bulletins de paie${providerName ? ` (${providerName})` : ''} : ${totaux?.nb_bulletins || 0} bulletins complets, cotisations patronales réelles.`;
  } else if (source === 'mixte') {
    message = `Consolidation salarié par salarié : ${nbReel} ligne(s) réelles complètes${nbPartiel ? `, ${nbPartiel} réelle(s) sans cotisations (estimées)` : ''}, ${nbEstime} estimée(s) à partir des salaires contractuels.`;
  } else if (source === 'theorique') {
    message = `Estimation : brut contractuel mensuel proratisé aux entrées/sorties, cotisations calculées par ${estimation?.methode_label?.toLowerCase() || 'les taux paramétrés'}. Aucun bulletin de paie n'est utilisé.`;
  } else {
    message = 'Aucune donnée disponible pour cette année (ni bulletin de paie, ni salarié présent).';
  }
  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border text-sm ${tone}`}>
      <Info className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="space-y-1">
        <p className="flex items-center gap-2 font-medium">Source des données : <SourceBadge source={source} /></p>
        <p>{message}</p>
        {estimation && (nbEstime > 0 || nbPartiel > 0 || source === 'theorique') && (
          <p className="text-xs opacity-90">
            Méthode d'estimation : {estimation.methode_label}
            {estimation.version_bareme ? ` · barème ${estimation.version_bareme}` : ''}
            {estimation.urssaf?.raison ? ` · ${estimation.urssaf.raison}` : ''}
            {' '}<Link to="/admin" className="underline font-medium">Paramétrer (Admin › Charges sociales)</Link>
          </p>
        )}
        {!connected && (
          <p>
            Aucun logiciel de paie n'est connecté.{' '}
            <Link to="/admin" className="underline font-medium">Configurer l'intégration paie</Link>
            {' '}pour importer les bulletins et leurs cotisations réelles.
          </p>
        )}
      </div>
    </div>
  );
};

const FamillesCard = ({ familles, brut }) => {
  const rows = familles || [];
  const totalPat = rows.reduce((acc, f) => acc + (f.patronal || 0), 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Cotisations par famille</CardTitle>
        <p className="text-xs text-slate-500">Réel (détail des bulletins) et estimé, cumulés sur l'année</p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune cotisation détaillée</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Famille</TableHead>
                <TableHead className="text-right">Part patronale</TableHead>
                <TableHead className="text-right">Part salariale</TableHead>
                <TableHead className="text-right">% du brut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((f) => (
                <TableRow key={f.famille}>
                  <TableCell className="font-medium">{f.label}</TableCell>
                  <TableCell className="text-right">{fmtEur(f.patronal, 2)}</TableCell>
                  <TableCell className="text-right">{fmtEur(f.salarial, 2)}</TableCell>
                  <TableCell className="text-right">{f.part_brut_pct !== null && f.part_brut_pct !== undefined ? fmtPct(f.part_brut_pct) : '—'}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 dark:bg-slate-800 font-semibold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{fmtEur(totalPat, 2)}</TableCell>
                <TableCell className="text-right">{fmtEur(rows.reduce((acc, f) => acc + (f.salarial || 0), 0), 2)}</TableCell>
                <TableCell className="text-right">{brut > 0 ? fmtPct((totalPat / brut) * 100) : '—'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

const EcartCard = ({ ecart, estimation }) => {
  const has = ecart && ecart.nb_lignes > 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Écart réel / estimé</CardTitle>
        <p className="text-xs text-slate-500">Sur les bulletins complets : cotisations patronales réelles comparées à l'estimation ({estimation?.methode_label?.toLowerCase() || 'estimation'})</p>
      </CardHeader>
      <CardContent>
        {!has ? (
          <p className="text-sm text-slate-500">Aucun bulletin complet sur la période : l'écart sera calculé dès que des cotisations réelles seront importées.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <p className="text-xs text-slate-500">Réel</p>
                <p className="font-semibold">{fmtEur(ecart.cotisations_patronales_reelles)}</p>
                <p className="text-xs text-slate-500">{fmtPct(ecart.taux_reel_pct)} du brut</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                <p className="text-xs text-slate-500">Estimé</p>
                <p className="font-semibold">{fmtEur(ecart.cotisations_patronales_estimees)}</p>
                <p className="text-xs text-slate-500">{fmtPct(ecart.taux_estime_pct)} du brut</p>
              </div>
              <div className={`p-3 rounded-lg ${Math.abs(ecart.ecart_pct || 0) <= 5 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                <p className="text-xs text-slate-500">Écart</p>
                <p className="font-semibold">{ecart.ecart > 0 ? '+' : ''}{fmtEur(ecart.ecart)}</p>
                <p className="text-xs text-slate-500">{ecart.ecart_pct !== null && ecart.ecart_pct !== undefined ? `${ecart.ecart_pct > 0 ? '+' : ''}${fmtPct(ecart.ecart_pct)}` : '—'} · {ecart.nb_lignes} bulletin(s)</p>
              </div>
            </div>
            {Object.keys(ecart.par_statut || {}).length > 0 && (
              <div className="space-y-1 text-sm">
                {Object.entries(ecart.par_statut).map(([statut, e]) => (
                  <div key={statut} className="flex justify-between gap-2">
                    <span className="capitalize text-slate-600 dark:text-slate-300">{statut}</span>
                    <span>{fmtPct(e.taux_reel_pct)} réel · {fmtPct(e.taux_estime_pct)} estimé · <span className={Math.abs(e.ecart_pct || 0) <= 5 ? 'text-emerald-700' : 'text-red-600'}>{e.ecart_pct !== null && e.ecart_pct !== undefined ? `${e.ecart_pct > 0 ? '+' : ''}${fmtPct(e.ecart_pct)}` : '—'}</span></span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500">Un écart supérieur à 5 % indique que le barème ou la convention collective paramétrés méritent un ajustement (Admin › Charges sociales).</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BreakdownCard = ({ title, data, total, capitalize = false }) => {
  const entries = Object.entries(data || {});
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune donnée</p>
        ) : (
          <div className="space-y-3">
            {entries.map(([label, d]) => (
              <div key={label} className="space-y-1">
                <div className="flex justify-between text-sm gap-2">
                  <span className={`font-medium truncate ${capitalize ? 'capitalize' : ''}`}>{label}</span>
                  <span className="shrink-0">{fmtEur(d.cout_employeur)} <span className="text-xs text-slate-500">({d.effectif} sal.)</span></span>
                </div>
                <Progress value={total > 0 ? Math.min(100, (d.cout_employeur / total) * 100) : 0} className="h-2" />
                <p className="text-xs text-slate-500">Brut {fmtEur(d.brut)} · Cot. patronales {fmtEur(d.cotisations_patronales)}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EstimationChargesCard = ({ data, onVoirCoutReel }) => {
  if (!data?.totaux) return null;
  const t = data.totaux;
  return (
    <Card className="lg:col-span-2 border-dashed">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg">Estimation des charges {data.annee}</CardTitle>
            <p className="text-xs text-slate-500">
              Masse théorique proratisée aux entrées/sorties · taux estimés : {fmtPct(data.taux_charges_patronales_estime)} patronal, {fmtPct(data.taux_charges_salariales_estime)} salarial (Admin › Configuration)
            </p>
          </div>
          {onVoirCoutReel && (
            <Button variant="outline" size="sm" onClick={onVoirCoutReel}>Voir le coût réel (paie)</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-2xl font-bold">{fmtEur(t.brut)}</p>
            <p className="text-sm text-slate-500">Brut théorique annuel</p>
          </div>
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-2xl font-bold">{fmtEur(t.cotisations_salariales)}</p>
            <p className="text-sm text-slate-500">Cotisations salariales estimées</p>
          </div>
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-2xl font-bold">{fmtEur(t.cotisations_patronales)}</p>
            <p className="text-sm text-slate-500">Cotisations patronales estimées</p>
          </div>
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-2xl font-bold text-[#0F5156]">{fmtEur(t.cout_employeur)}</p>
            <p className="text-sm text-slate-500">Coût employeur estimé</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Reporting;
